/**
 * GlobalSamplesPanel - Shows samples for the current active plugin
 * Integrates with global samplesEnabled state
 */

import React from 'react';
import { SamplesPanel } from './SamplesPanel';
import { getEnabledPlugins } from '../pluginRegistry';
import type { ToolType } from '../types';

interface GlobalSamplesPanelProps {
  currentTool: ToolType;
  onClose: () => void;
  onLoadSample: (sample: any) => void;
}

export function GlobalSamplesPanel({
  currentTool,
  onClose,
  onLoadSample
}: GlobalSamplesPanelProps) {
  // Get the current plugin
  const plugins = getEnabledPlugins();
  const currentPlugin = plugins.find(p => p.id === currentTool);

  if (!currentPlugin) {
    return null;
  }

  // Get samples from plugin manifest
  const samples = (currentPlugin as any).samples || [];

  if (samples.length === 0) {
    return (
      <div className="global-samples-empty">
        <div className="empty-content">
          <span className="empty-icon">📦</span>
          <h3>No Samples Available</h3>
          <p>This tool doesn't have any sample data defined yet.</p>
          <button onClick={onClose} className="btn btn-primary">Close</button>
        </div>
        <style>{`
          .global-samples-empty {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .empty-content {
            background: var(--surface);
            border-radius: 12px;
            padding: 3rem;
            text-align: center;
            max-width: 400px;
          }
          
          .empty-icon {
            font-size: 4rem;
            display: block;
            margin-bottom: 1rem;
          }
          
          .empty-content h3 {
            margin: 0 0 0.5rem 0;
            color: var(--text);
          }
          
          .empty-content p {
            color: var(--text-secondary);
            margin: 0 0 1.5rem 0;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="global-samples-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
        <SamplesPanel
          samples={samples}
          onLoadSample={onLoadSample}
          onClose={onClose}
          title={`${currentPlugin.name} Samples`}
        />
      </div>
      <style>{`
        .global-samples-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
      `}</style>
    </div>
  );
}
